import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button} from "antd";
import {CloseOutlined, DislikeOutlined} from "@ant-design/icons";
import {InterestFeedbackOption} from "../../domain/InterestMemory";
import VulSourceItem from "../../domain/VulSourceItem";
import VulSourceItemListTable from "../vulsourceitem/VulSourceItemListTable";
import VulReportInterestFeedback from "./VulReportInterestFeedback";
import * as styles from "./VulReportItems.module.css";

type Props = Omit<React.ComponentProps<typeof VulSourceItemListTable>, "rowSelection"> & {
    reportId: string;
    interests: InterestFeedbackOption[];
    children: React.ReactNode;
};

function VulReportItems({reportId, interests, children, ...tableProps}: Props) {
    const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const eligibleItemIds = useMemo(() => new Set(interests.flatMap(interest => interest.itemIds)), [interests]);
    const canSelectItems = eligibleItemIds.size > 0;
    const rowSelection = useMemo(() => ({
        selectedRowKeys: selectedItemIds,
        preserveSelectedRowKeys: true,
        onChange: (keys: React.Key[]) => setSelectedItemIds(keys.map(String)),
        getCheckboxProps: (item: VulSourceItem) => ({disabled: !eligibleItemIds.has(item.id)})
    }), [selectedItemIds, eligibleItemIds]);
    const onSaved = useCallback(() => {
        setFeedbackOpen(false);
        setSelectedItemIds([]);
    }, []);

    useEffect(() => {
        setSelectedItemIds(selectedIds => {
            const eligibleSelectedIds = selectedIds.filter(id => eligibleItemIds.has(id));

            return eligibleSelectedIds.length === selectedIds.length ? selectedIds : eligibleSelectedIds;
        });
    }, [eligibleItemIds]);

    return <>
        {children}

        <div className={canSelectItems ? styles.items : undefined}>
            <VulSourceItemListTable
                {...tableProps}
                rowSelection={canSelectItems ? rowSelection : undefined}
            />
        </div>

        {canSelectItems && selectedItemIds.length > 0 && <div className={styles.selectionBar}
            role="toolbar" aria-label="Selected report items">
            <span className={styles.selectionCount} aria-live="polite">Selected: {selectedItemIds.length}</span>
            <Button type="primary" icon={<DislikeOutlined/>}
                    disabled={!selectedItemIds.some(id => eligibleItemIds.has(id))}
                    onClick={() => setFeedbackOpen(true)}>Not interested</Button>
            <Button icon={<CloseOutlined/>} onClick={() => setSelectedItemIds([])}>Clear selection</Button>
        </div>}

        {feedbackOpen && <VulReportInterestFeedback
            key={reportId}
            reportId={reportId}
            interests={interests}
            selectedItemIds={selectedItemIds}
            onSaved={onSaved}
            onCancel={() => setFeedbackOpen(false)}
        />}
    </>;
}

export default VulReportItems;
